import { useEffect } from 'react';
import { Button } from './Button';
import { isTelegramWebApp } from '../../utils';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  filename: string;
}

/**
 * Modal for displaying an image when direct download isn't available
 * (e.g., in Telegram WebApp). Shows the image with instructions for saving.
 */
export function ImagePreviewModal({
  isOpen,
  onClose,
  imageUrl,
  filename,
}: ImagePreviewModalProps) {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !imageUrl) return null;

  const inTelegram = isTelegramWebApp();

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full mx-4 max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border dark:border-slate-700">
          <h2 className="text-lg font-semibold text-text-dark dark:text-slate-100">
            Save Your Image
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-text-muted dark:text-slate-400 hover:text-text-dark dark:hover:text-slate-200 hover:bg-surface-secondary dark:hover:bg-slate-800 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Instructions */}
          <div className="bg-game-blue/10 dark:bg-game-blue/20 rounded-lg p-3 border border-game-blue/30">
            <p className="text-sm text-text-dark dark:text-slate-200 text-center">
              {inTelegram ? (
                <>
                  <strong>Tap and hold</strong> the image below, then select{' '}
                  <strong>"Save Image"</strong> to download
                </>
              ) : (
                <>
                  <strong>Long press</strong> or <strong>right-click</strong> the image
                  to save it
                </>
              )}
            </p>
          </div>

          {/* Image Preview */}
          <div className="flex justify-center">
            <img
              src={imageUrl}
              alt={filename}
              className="max-w-full max-h-[50vh] rounded-lg shadow-lg border border-surface-border dark:border-slate-600 object-contain"
              style={{ touchAction: 'manipulation' }}
            />
          </div>

          {/* Filename */}
          <p className="text-xs text-text-muted dark:text-slate-400 text-center truncate">
            {filename}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center px-4 py-3 border-t border-surface-border dark:border-slate-700">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
